import { cn, capitalize } from '@express-labs/raven-tools';
import React, {
  type ElementType,
  type ComponentPropsWithRef,
  type JSXElementConstructor,
  type Ref,
} from 'react';
import s from './AtomicButton.module.scss';

// polymorphic component with forwarded ref thanks to
// https://www.tsteele.dev/posts/react-polymorphic-forwardref
// https://gist.github.com/kripod/4434e7cecfdecee160026aee49ea6ee8

type IntrinsicAttributes<
  E extends keyof JSX.IntrinsicElements | JSXElementConstructor<any>,
> = JSX.LibraryManagedAttributes<E, ComponentPropsWithRef<E>>;

interface AtomicButtonOwnProps<E extends ElementType = ElementType> {
  /** Specify the HTML tag to render for the button.  The default tag is "button".  Only "button" and "a" are officially supported via CSS. Other tags may require more CSS. */
  as?: E;
  /** Force the button to display as "selected" or "inactive" */
  buttonState?: 'pressed' | 'inactive';
  /** Any additional class or classes to further customize the button appearance. */
  className?: string;
  /** Show the button as a solid button or as a hollow "ghost" button */
  mode?: 'solid' | 'ghost';
  /** Buttons come in three sizes */
  size?: 'mega' | 'large' | 'small';
  /** The color theme for the button. */
  theme?: 'light' | 'dark';
  /** Child content and/or JSX to render inside the HTML tags. */
  children?: React.ReactNode;
}

export type AtomicButtonProps<E extends ElementType> = AtomicButtonOwnProps<E> &
  Omit<IntrinsicAttributes<E>, keyof AtomicButtonOwnProps>;

export const AtomicButton = React.forwardRef(function AtomicButton(
  {
    as: Element = 'button',
    buttonState,
    children,
    className,
    mode = 'solid',
    size = 'large',
    theme = 'dark',
    ...restProps
  }: AtomicButtonOwnProps,
  ref: Ref<Element>
) {
  // Example: 'solidDark', 'ghostDark', 'solidLight', 'ghostLight' 👻
  const classNameThemeMode = `${mode}${capitalize(theme)}`;

  return (
    <Element
      ref={ref}
      className={cn(
        'AtomicButton',
        s.btn,
        s[classNameThemeMode],
        s[size],
        buttonState && s[buttonState],
        Element === 'a' && s.noTextDecoration,
        className
      )}
      {...restProps}
    >
      {children}
    </Element>
  );
}) as <E extends ElementType = 'button'>(
  props: AtomicButtonProps<E>
) => JSX.Element;

export default AtomicButton;
